import { ConfigProvider, theme } from 'antd';
import PhoneInput from 'antd-phone-input';
import { paraE164 } from './telefone';

/**
 * Campo de telefone com seletor de país.
 *
 * O desenho vem da Voa: bandeira e seta num seletor estreito colado à
 * esquerda, o número mascarado ao lado, tudo num `Space.Compact` que parece um
 * campo só. Quem digita não precisa saber o formato - a máscara do país
 * escolhido se encarrega, e o `(11) 98765-4321` sai de graça.
 *
 * **O valor que entra e sai daqui é E.164** (`+5511987654321`): dígitos, código
 * do país na frente, nada de pontuação. A máscara é da tela; o banco guarda a
 * forma que gateway de pagamento e API de mensagem sabem ler.
 *
 * Os nomes dos países saem em português sem nenhuma configuração aqui: a
 * biblioteca lê o `locale` do `ConfigProvider`, que o `TitularisProvider` já
 * define como pt-BR. Trocar o idioma do app troca o desta lista junto.
 */

/** País pré-selecionado. O produto é de concurso brasileiro. */
const PAIS_PADRAO = 'br';

export interface CampoDeTelefoneProps {
  /** Telefone em E.164, ou nulo/vazio quando não há. */
  value?: string | null;
  /**
   * Chamado a cada tecla, com o telefone em E.164 (ou `null` se o campo ficou
   * vazio) e se ele já forma um número **completo** para o país escolhido.
   */
  onChange?: (telefone: string | null, valido: boolean) => void;
  onBlur?: () => void;
  disabled?: boolean;
  status?: 'error';
  /** Largura do conjunto. O padrão acomoda o celular brasileiro sem folga. */
  width?: number;
  'aria-label'?: string;
}

export function CampoDeTelefone({
  value,
  onChange,
  onBlur,
  disabled,
  status,
  width = 232,
  'aria-label': ariaLabel = 'Telefone',
}: CampoDeTelefoneProps) {
  const { token } = theme.useToken();

  /*
   * A largura mora aqui fora porque a folha de estilo da biblioteca manda o
   * `Space.Compact` ocupar 100% do que o envolve. Sem este contêiner, o campo
   * estica até o fim do cartão.
   *
   * **O vermelho do erro vem por token, e não pela propriedade `status`.** A
   * biblioteca repassa `status` só ao campo do número: o seletor de país fica
   * cinza e o controle - que se apresenta como um só - sai metade vermelho,
   * metade não. O caminho do Antd para isso é o contexto de validação de um
   * `Form.Item`, e ele não serve aqui: fora de um `Form` ele avisa no console
   * e, medido no navegador em 03/09/2026, **engole o que se digita**. Redefinir
   * a cor de borda nesta subárvore pinta os dois lados sem nada disso.
   */
  const bordas = {
    token: {
      colorBorder: token.colorErrorBorder,
      colorPrimaryHover: token.colorErrorBorderHover,
      colorPrimary: token.colorError,
    },
  };

  return (
    <ConfigProvider theme={status === 'error' ? bordas : undefined}>
      <div style={{ width }}>
        <PhoneInput
          // Sem valor gravado o campo nasce no país padrão, e não no do fuso
          // horário do aparelho - que é o que a biblioteca faria sozinha.
          value={value ?? ''}
          country={PAIS_PADRAO}
          disabled={disabled}
          enableSearch
          enableArrow
          /*
           * Um item por país, não um por código de área. Sem isto, os Estados
           * Unidos aparecem centenas de vezes na lista - um para cada DDD -, e
           * rolar até o Brasil vira exercício de paciência.
           */
          distinct
          aria-label={ariaLabel}
          status={status}
          onBlur={onBlur}
          /*
           * Validação **estrita**, e isto foi medido no navegador em 03/09/2026.
           *
           * A frouxa é `^\d{8,11}$` para o Brasil: ela aprova `(11) 98765-4`,
           * oito dígitos, como se fosse celular. O campo dava o número por
           * completo e o botão de salvar acendia com metade do telefone
           * digitado. A estrita confere a faixa do DDD e o tamanho por país -
           * aprova fixo de dez dígitos e celular de onze, recusa o que falta.
           */
          onChange={(valor) => onChange?.(paraE164(valor), valor.valid?.(true) ?? false)}
        />
      </div>
    </ConfigProvider>
  );
}
